import { useCallback, useEffect, useMemo, useState } from 'react';

import { KEYS } from '@highlight-cp/constants';

import type { AtMentionOption, CursorPosition } from '../../types';
import { createAtMentionBadge, getCursorPosition as getCursorPositionUtil } from '../../utils';

interface UseAtMentionsProps {
  options?: AtMentionOption[];
  spanRef: React.RefObject<HTMLSpanElement | null>;
}

export interface UseAtMentionsReturn {
  isOpen: boolean;
  filteredOptions: AtMentionOption[];
  selectedIndex: number;
  cursorPosition: CursorPosition;
  handleInput: () => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLSpanElement>) => boolean;
  insertMention: (option: AtMentionOption) => void;
  closeMentionMenu: () => void;
}

/**
 * ### useAtMentions
 *
 * @description
 * Hook for handling @-mention functionality in contentEditable elements.
 * Detects @ symbol input and manages mention dropdown state.
 *
 * @param props - The props for the hook
 * @returns Mention state and handlers
 */
const useAtMentions = ({ options = [], spanRef }: UseAtMentionsProps): UseAtMentionsReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState<CursorPosition>({ x: 0, y: 0 });
  const [mentionStartOffset, setMentionStartOffset] = useState<number | null>(null);

  // Filter options based on current filter text
  const filteredOptions = useMemo(() => {
    if (!filterText) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(filterText.toLowerCase()),
    );
  }, [options, filterText]);

  // Get cursor position for dropdown placement
  const getCursorPosition = useCallback(getCursorPositionUtil, []);

  // Check if @ was typed and open menu
  const handleInput = useCallback(() => {
    if (!spanRef.current || !options || options.length === 0) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const textContent = spanRef.current.textContent || '';

    // Calculate cursor position relative to the entire contentEditable
    // by walking through all text nodes up to the cursor
    let cursorOffset = 0;
    const container = range.startContainer;
    const offsetInNode = range.startOffset;

    // Walk through all nodes before the cursor to calculate total offset
    // Also track the last @ position we encounter
    const walker = document.createTreeWalker(spanRef.current, NodeFilter.SHOW_TEXT, null);

    let lastAtIndex = -1;

    let currentNode = walker.nextNode();
    while (currentNode) {
      if (currentNode === container) {
        cursorOffset += offsetInNode;

        // Check if @ is in the current node (the one with the cursor)
        const textUpToCursor = currentNode.textContent?.substring(0, offsetInNode) || '';
        const atIndexInNode = textUpToCursor.lastIndexOf('@');
        if (atIndexInNode !== -1) {
          lastAtIndex = cursorOffset - offsetInNode + atIndexInNode;
        }
        break;
      }

      // Check if @ is in this node
      const nodeText = currentNode.textContent || '';
      const atIndexInNode = nodeText.lastIndexOf('@');
      if (atIndexInNode !== -1) {
        lastAtIndex = cursorOffset + atIndexInNode;
      }

      cursorOffset += nodeText.length;
      currentNode = walker.nextNode();
    }

    // Find the text between @ and cursor
    const textBeforeCursor = textContent.substring(0, cursorOffset);

    if (lastAtIndex !== -1) {
      // Check if there's a space between @ and cursor (if so, close menu)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
        setIsOpen(false);
        setFilterText('');
        setMentionStartOffset(null);
        return;
      }

      // Open menu and set filter text
      const position = getCursorPosition();
      if (position) {
        setCursorPosition(position);
        setFilterText(textAfterAt);
        setMentionStartOffset(lastAtIndex);
        setIsOpen(true);
        setSelectedIndex(0);
      }
    } else {
      setIsOpen(false);
      setFilterText('');
      setMentionStartOffset(null);
    }
  }, [spanRef, getCursorPosition, options]);

  // Insert selected mention
  const insertMention = useCallback(
    (option: AtMentionOption) => {
      if (!spanRef.current || mentionStartOffset === null) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // Create badge element
      const badge = createAtMentionBadge(spanRef.current, option);

      // Find the text node and position where @ starts
      // Walk through text nodes to find the one containing the @ symbol
      let currentOffset = 0;
      let targetNode: Node | null = null;
      let offsetInNode = 0;

      const walker = document.createTreeWalker(spanRef.current, NodeFilter.SHOW_TEXT, null);
      let textNode = walker.nextNode();

      while (textNode) {
        const nodeLength = textNode.textContent?.length || 0;
        if (currentOffset + nodeLength > mentionStartOffset) {
          // Found the node containing the @
          targetNode = textNode;
          offsetInNode = mentionStartOffset - currentOffset;
          break;
        }
        currentOffset += nodeLength;
        textNode = walker.nextNode();
      }

      if (!targetNode?.textContent) {
        return;
      }

      // Create a range from @ position to current cursor to delete the text in between
      const deleteRange = document.createRange();

      // Set start of range to @ position
      deleteRange.setStart(targetNode, offsetInNode);

      // Set end of range to current cursor position
      deleteRange.setEnd(range.startContainer, range.startOffset);

      // Delete from @ to cursor - this collapses the range to the start position (@)
      deleteRange.deleteContents();

      // The deleteRange is now collapsed at the @ position - use it to insert
      // Insert badge at the collapsed position
      deleteRange.insertNode(badge);

      // Insert space after badge
      const spaceNode = document.createTextNode(' ');
      deleteRange.setStartAfter(badge);
      deleteRange.insertNode(spaceNode);

      // Set cursor after the space
      const newRange = document.createRange();
      const newSelection = window.getSelection();
      newRange.setStartAfter(spaceNode);
      newRange.collapse(true);
      newSelection?.removeAllRanges();
      newSelection?.addRange(newRange);

      // Close menu
      setIsOpen(false);
      setFilterText('');
      setMentionStartOffset(null);

      // Trigger input event to save changes
      const inputEvent = new Event('input', { bubbles: true });
      spanRef.current?.dispatchEvent(inputEvent);
    },
    [spanRef, mentionStartOffset],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (!isOpen || filteredOptions.length === 0) return false;

      switch (event.key) {
        case KEYS.ARROW_DOWN:
          event.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, filteredOptions.length - 1));
          return true;

        case KEYS.ARROW_UP:
          event.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          return true;

        case KEYS.ENTER:
        case KEYS.TAB:
          event.preventDefault();
          if (filteredOptions[selectedIndex]) {
            insertMention(filteredOptions[selectedIndex]);
          }
          return true;

        case KEYS.ESCAPE:
          event.preventDefault();
          setIsOpen(false);
          setFilterText('');
          setMentionStartOffset(null);
          return true;

        default:
          return false;
      }
    },
    [isOpen, filteredOptions, selectedIndex, insertMention],
  );

  // Reset selected index when filtered options change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filterText]);

  return {
    isOpen,
    filteredOptions,
    selectedIndex,
    cursorPosition,
    handleInput,
    handleKeyDown,
    insertMention,
    closeMentionMenu: () => {
      setIsOpen(false);
      setFilterText('');
      setMentionStartOffset(null);
    },
  };
};

export default useAtMentions;
